/**
 * NOVA-023 — BreakGlassReviewModal. The manager/medical-director post-hoc review of a duty-to-warn
 * override (VAL-3). Shows the sealed override (justification + opaque refs) and captures the recorded
 * judgement: a mandatory `outcome` verdict + optional notes. The server seals the close in the
 * tamper-evident chain (BREAK_GLASS_REVIEW). Gated by the caller on `validation.valeurcritique.review`;
 * the server refuses an outcome-less close, mirrored client-side. Not dismissable while submitting.
 */
import { useEffect, useState } from "react";
import { Badge, Button, FormField, Modal, Select, Textarea } from "@/ui";
import { useT } from "@/app";
import { token } from "@/design/theme";
import type { ValidationBreakGlass } from "../types";

export interface BreakGlassReviewModalProps {
  open: boolean;
  breakGlass: ValidationBreakGlass | null;
  onClose: () => void;
  onConfirm: (input: { outcome: string; notes?: string }) => Promise<void>;
  submitting?: boolean;
}

/** The recorded review verdicts (config-mirrored vocabulary — the server stores the chosen string). */
const OUTCOMES = ["justifie", "non_justifie", "a_surveiller"] as const;

export function BreakGlassReviewModal({ open, breakGlass, onClose, onConfirm, submitting }: BreakGlassReviewModalProps) {
  const { t } = useT();
  const [outcome, setOutcome] = useState<string>(OUTCOMES[0]);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (open) {
      setOutcome(OUTCOMES[0]);
      setNotes("");
    }
  }, [open]);

  const invalid = outcome.trim().length === 0;

  const submit = async () => {
    if (invalid || submitting) return;
    await onConfirm({ outcome: outcome.trim(), ...(notes.trim() ? { notes: notes.trim() } : {}) });
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissable={!submitting}
      title={t("validation.bgreview.title")}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            {t("validation.cancel")}
          </Button>
          <Button onClick={() => void submit()} loading={submitting === true} disabled={invalid}>
            {t("validation.bgreview.submit")}
          </Button>
        </>
      }
    >
      <div style={{ display: "flex", flexDirection: "column", gap: token.s4 }}>
        {breakGlass && (
          <div style={{ display: "flex", flexDirection: "column", gap: token.s2, padding: token.s3, borderRadius: token.rSm, border: `1px solid ${token.lineFaint}` }}>
            <div style={{ display: "flex", gap: token.s2, alignItems: "center", flexWrap: "wrap" }}>
              <Badge tone="warn">{t("validation.bgreview.pending")}</Badge>
              <span className="nv-mono nv-ltr-island" style={{ color: token.steel }}>#{String(breakGlass.id)}</span>
              <span className="nv-mono nv-ltr-island" style={{ color: token.steel }}>
                {t("validation.bgreview.fromEscalade", { id: String(breakGlass.escalade_critique_id) })}
              </span>
            </div>
            <p style={{ margin: 0, color: token.ink, fontSize: token.fsLabel }}>
              <strong>{t("validation.bgreview.justification")}: </strong>
              {breakGlass.justification}
            </p>
          </div>
        )}
        <FormField label={t("validation.bgreview.outcome")} hint={t("validation.bgreview.outcome.hint")} required>
          {(p) => (
            <Select {...p} value={outcome} onChange={(e) => setOutcome(e.target.value)}>
              {OUTCOMES.map((o) => (
                <option key={o} value={o}>
                  {t(`validation.bgreview.outcome.${o}`)}
                </option>
              ))}
            </Select>
          )}
        </FormField>
        <FormField label={t("validation.bgreview.notes")} hint={t("validation.bgreview.notes.hint")}>
          {(p) => <Textarea {...p} value={notes} onChange={(e) => setNotes(e.target.value)} rows={3} />}
        </FormField>
      </div>
    </Modal>
  );
}
